import React, { useState, useEffect } from 'react';
import { saveFragment, deleteFragment, listSystemTemplates, listTemplates } from '../services/templateService';

/**
 * Editor modal untuk preset fragment (hook / background).
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onSaved: () => void
 *   - moduleId: string
 *   - type: 'hooks' | 'backgrounds'
 *   - fragment: object | null (null = new)
 */
export default function FragmentEditorModal({ open, onClose, onSaved, moduleId, type, fragment = null }) {
    const isEdit = !!fragment;
    const typeLabel = type === 'hooks' ? 'Hook' : type === 'backgrounds' ? 'Background' : type;
    const [name, setName] = useState('');
    const [fragmentText, setFragmentText] = useState('');
    const [examplesText, setExamplesText] = useState(''); // 1 line per example
    const [order, setOrder] = useState(99);
    const [enabled, setEnabled] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setName(fragment?.name || '');
        setFragmentText(fragment?.fragment || '');
        setExamplesText(Array.isArray(fragment?.examples) ? fragment.examples.join('\n') : '');
        setOrder(typeof fragment?.order === 'number' ? fragment.order : 99);
        setEnabled(fragment?.enabled !== false);
        setError('');
    }, [open, fragment]);

    if (!open) return null;

    const handleSave = async () => {
        if (!name.trim()) { setError('Nama fragment wajib.'); return; }
        if (!fragmentText.trim()) { setError('Fragment description wajib.'); return; }
        setSaving(true);
        setError('');
        try {
            const examples = examplesText.split('\n').map(s => s.trim()).filter(Boolean);
            await saveFragment(moduleId, type, {
                id: fragment?.id,
                name: name.trim(),
                fragment: fragmentText.trim(),
                examples,
                order: Number(order) || 99,
                enabled
            });
            onSaved?.();
            onClose?.();
        } catch (err) {
            console.error('[FragmentEditor] Save error:', err);
            setError(err.message || 'Gagal simpan.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!isEdit) return;
        setSaving(true);
        setError('');
        try {
            // ★ A3 fix: Check usage dalam system templates SEBELUM padam.
            // Field check based on type (hookId for hooks, bgId for backgrounds).
            const idField = type === 'hooks' ? 'hookId' : 'bgId';
            const usedInTemplates = [];
            try {
                const sysList = await listSystemTemplates(moduleId);
                for (const t of sysList) {
                    const used = ['set1', 'set2', 'set3'].some(sid => t[sid]?.[idField] === fragment.id);
                    if (used) usedInTemplates.push(t.name);
                }
            } catch (e) {
                console.warn('[FragmentEditor] Usage check failed (proceed without):', e);
            }

            // Build warning message
            let warningMsg = `Padam ${typeLabel} "${fragment.name}"?\n\nTindakan ini TIDAK BOLEH UNDO.`;
            if (usedInTemplates.length > 0) {
                warningMsg = `⚠ AMARAN — ${typeLabel} "${fragment.name}" sedang digunakan oleh ${usedInTemplates.length} template:\n\n` +
                    usedInTemplates.map(n => `  • ${n}`).join('\n') +
                    `\n\nKalau padam, template-template ni akan generate dengan ${typeLabel.toLowerCase()}Text kosong (Cloud Function fallback).\n\nPastikan kau audit + edit template tu dulu.\n\nConfirm padam?`;
            }

            if (!confirm(warningMsg)) {
                setSaving(false);
                return;
            }

            await deleteFragment(moduleId, type, fragment.id);
            onSaved?.();
            onClose?.();
        } catch (err) {
            console.error('[FragmentEditor] Delete error:', err);
            setError(err.message || 'Gagal padam.');
            setSaving(false);
        }
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#ffffff', borderRadius: 14, padding: 20, width: 520, maxWidth: '92vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#ecfeff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#0891b2' }}>extension</span>
                    </div>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>{isEdit ? `Edit ${typeLabel}` : `${typeLabel} Baru`}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>Preset fragment — admin manage</div>
                    </div>
                </div>

                <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Nama (display)</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Cth: Urgency Time"
                    style={{ width: '100%', padding: '8px 10px', fontSize: 12, fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 12, boxSizing: 'border-box' }} />

                <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Fragment Description</label>
                <textarea value={fragmentText} onChange={e => setFragmentText(e.target.value)} rows={4}
                    placeholder="Cth: Hook ayat tentang masa cepat habis. Tone: urgent, push checkout NOW. Fokus pada deadline (hari ini, malam ini, esok). JANGAN storytelling panjang."
                    style={{ width: '100%', padding: '10px 12px', fontSize: 11, fontWeight: 500, border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 4, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                <div style={{ fontSize: 9, color: '#94a3b8', marginBottom: 12 }}>Akan dibekalkan ke AI sebagai panduan style untuk fragment ni.</div>

                <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Examples (1 per line)</label>
                <textarea value={examplesText} onChange={e => setExamplesText(e.target.value)} rows={4}
                    placeholder={'Hari ni je murah, esok dah naik!\nLast day promo — lepas ni habis!\nPromo abis pukul 12 malam ni'}
                    style={{ width: '100%', padding: '10px 12px', fontSize: 11, fontWeight: 500, border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 4, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                <div style={{ fontSize: 9, color: '#94a3b8', marginBottom: 12 }}>AI akan rujuk sebagai inspirasi (jangan copy verbatim — adapt ikut produk).</div>

                <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Order (sort)</label>
                <input type="number" value={order} onChange={e => setOrder(e.target.value)}
                    style={{ width: 100, padding: '6px 8px', fontSize: 11, fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 14, boxSizing: 'border-box' }} />

                {error && (
                    <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: 8, fontSize: 11, marginBottom: 10 }}>⚠ {error}</div>
                )}

                {isEdit && !enabled && (
                    <div style={{ background: '#f1f5f9', color: '#475569', borderRadius: 6, padding: 8, fontSize: 10, fontWeight: 600, marginBottom: 10 }}>
                        🙈 {typeLabel} ni akan disembunyikan dari pilihan user. Klik <b>Simpan</b> untuk sahkan. (Admin masih nampak di sini.)
                    </div>
                )}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 14 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {isEdit && (
                            <button onClick={handleDelete} disabled={saving}
                                style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, color: '#dc2626', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer' }}>
                                🗑 Padam
                            </button>
                        )}
                        {isEdit && (
                            <button onClick={() => setEnabled(v => !v)} disabled={saving}
                                style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, color: enabled ? '#b45309' : '#15803d', background: enabled ? '#fef3c7' : '#dcfce7', border: `1px solid ${enabled ? '#fde68a' : '#bbf7d0'}`, borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer' }}>
                                {enabled ? '🙈 Sembunyikan' : '👁 Tunjuk semula'}
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={onClose} disabled={saving}
                            style={{ padding: '8px 14px', fontSize: 11, fontWeight: 600, color: '#64748b', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer' }}>Batal</button>
                        <button onClick={handleSave} disabled={saving}
                            style={{ padding: '8px 16px', fontSize: 11, fontWeight: 800, color: '#ffffff', background: '#0891b2', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px rgba(8,145,178,0.3)' }}>
                            {saving ? 'Saving...' : 'Simpan'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
